import { redactOnlineJoinDebugValue } from "./debug-redaction";

export { redactOnlineJoinDebugValue };

export type OnlineJoinDebugSnapshot = {
  brand?: string;
  stage?: string;
  routePath?: string;
  updatedAt?: string;
  selections?: unknown;
  formState?: unknown;
  abcRequest?: {
    method?: string;
    url?: string;
    payload?: unknown;
    note?: string;
    updatedAt?: string;
  };
};

const STORAGE_KEY = "onlineJoinDebugSnapshot";

declare global {
  interface Window {
    __onlineJoinDebugSnapshot?: OnlineJoinDebugSnapshot;
    __onlineJoinDebugSubscribers?: Set<() => void>;
  }
}

function readStoredSnapshot(): OnlineJoinDebugSnapshot | null {
  if (typeof window === "undefined") return null;
  if (window.__onlineJoinDebugSnapshot) return window.__onlineJoinDebugSnapshot;

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OnlineJoinDebugSnapshot;
    window.__onlineJoinDebugSnapshot = parsed;
    return parsed;
  } catch {
    return null;
  }
}

function notifySubscribers() {
  if (typeof window === "undefined") return;
  window.__onlineJoinDebugSubscribers?.forEach((listener) => listener());
}

export function getOnlineJoinDebugSnapshot(): OnlineJoinDebugSnapshot | null {
  return readStoredSnapshot();
}

export function setOnlineJoinDebugSnapshot(snapshot: OnlineJoinDebugSnapshot) {
  if (typeof window === "undefined") return;

  const now = new Date().toISOString();
  const next: OnlineJoinDebugSnapshot = {
    ...(readStoredSnapshot() ?? {}),
    ...snapshot,
    selections: snapshot.selections
      ? redactOnlineJoinDebugValue(snapshot.selections)
      : snapshot.selections,
    formState: snapshot.formState
      ? redactOnlineJoinDebugValue(snapshot.formState)
      : snapshot.formState,
    abcRequest: snapshot.abcRequest
      ? {
          ...snapshot.abcRequest,
          payload: redactOnlineJoinDebugValue(snapshot.abcRequest.payload),
          updatedAt: snapshot.abcRequest.updatedAt ?? now,
        }
      : snapshot.abcRequest,
    updatedAt: now,
  };

  window.__onlineJoinDebugSnapshot = next;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Debug state should never affect the funnel.
  }
  notifySubscribers();
}

export function subscribeOnlineJoinDebugSnapshot(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.__onlineJoinDebugSubscribers ??= new Set();
  window.__onlineJoinDebugSubscribers.add(listener);
  return () => {
    window.__onlineJoinDebugSubscribers?.delete(listener);
  };
}
